import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core'
import { NavService } from '../../services/nav.service'
import { ActivatedRoute, Router } from '@angular/router'
import { first, takeUntil } from 'rxjs/operators'
import { KeyingAuthService } from '../../services/keying.auth.service'
import { Subject } from 'rxjs'
import { KeySystem, Structure, StructureDetails } from '../../models/dev.test.models'
import { Loading, LoadingService } from '../../services/loading.service'
import { fadeIn } from '../../animations/fadeIn'
import { KeyingStructureService } from '../../services/keying.structure.service'
import { KeyingSystemService } from '../../services/keying.system.service'
import { KeyingDataSets, KeyingDataSetsService, KeySystemDataSets } from '../../services/keying.data.sets.service'
import { ThirdPartyNoticeComponent } from '@overtur-monorepo/phx-angular-material'
import { MatDialog } from '@angular/material/dialog'
import { ErrorService } from '../../services/error.service'
import { AppRoutingConfig } from '../../configuration/app.routing.config'

@Component({
    selector: 'app-key-system',
    templateUrl: './key-system.component.html',
    styleUrls: ['./key-system.component.scss'],
    animations: [fadeIn],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KeySystemComponent implements OnInit, OnDestroy {
  destroy$ = new Subject()
  loading: Loading

  title = 'Keying'
  user
  structureId: number
  keySystemId: number
  structure: Structure
  keySystem: KeySystem
  keySystemDataSets: KeySystemDataSets
  keyingDataSets: KeyingDataSets
  keywayFamily
  combinatingRule

  navButtons = []

  userProfileActions = [{ name: 'logout', icon: 'logout', id: 0 }]

  helpActions = [
    { name: 'Support', icon: 'user', id: 0 },
    { name: 'Third party notice', icon: 'info', id: 1 },
  ]

  disableMecsBtn = true
  mecsToolTipMessage = ''

  private isStructureLoadedFromCache = false

  constructor(
    private navService: NavService,
    private route: ActivatedRoute,
    private keyingAuthService: KeyingAuthService,
    private loadingService: LoadingService,
    private keyingStructureService: KeyingStructureService,
    private keyingSystemService: KeyingSystemService,
    private keyingDataSetsService: KeyingDataSetsService,
    private errorService: ErrorService,
    private router: Router,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef
  ) {}

  /* istanbul ignore next */
  ngOnInit(): void {
    this.loadingService.loading$.pipe(takeUntil(this.destroy$)).subscribe((loading) => {
      this.loading = loading
      this.cdr.markForCheck()
    })

    this.keyingAuthService.keyingUser$.pipe(takeUntil(this.destroy$)).subscribe((user) => {
      if (user) {
        this.user = user
        this.cdr.markForCheck()
      }
    })

    this.keyingStructureService.structure$.pipe(takeUntil(this.destroy$)).subscribe((structure) => {
      if (!structure) {
        return
      }
      this.structure = structure
      this.cdr.markForCheck()
      if (!this.isStructureLoadedFromCache) {
        this.isStructureLoadedFromCache = true
        // If StructureId exists, then continue to process post structure load operations, else wait for StructureId to render
        if (this.structureId) {
          this.onStructureLoad(structure)
        } else {
          const interval = setInterval(() => {
            if (this.structureId) {
              clearInterval(interval)
              this.onStructureLoad(structure)
            }
          }, 2000)
        }
      } else {
        this.isStructureLoadedFromCache = true
        this.keyingSystemService.keySystem$.pipe(takeUntil(this.destroy$)).subscribe((keySystem) => {
          if (!keySystem) {
            return
          }
          this.keySystem = keySystem
          this.cdr.markForCheck()
        })

        /* Enable Disable MECS */
        this.enableDisableMECS()
      }
    })
    this.route.params.pipe(first()).subscribe((res) => {
      this.structureId = res.structureId
      this.keySystemId = res.keySystemId
      this.keyingStructureService.setStructureId(this.structureId)
      this.navService.setProfileActions(this.userProfileActions)
      this.navService.setHelpActions(this.helpActions)

      if (!this.isStructureLoadedFromCache) {
        this.keyingStructureService
          .getStructure(this.structureId)
          .pipe(takeUntil(this.destroy$))
          .subscribe((structure) => {
            this.onStructureLoad(structure)
          })
      }
    })

    this.keyingDataSetsService.keySystemDataSets$.pipe(takeUntil(this.destroy$)).subscribe((res) => {
      if (!res) {
        return
      }
      this.keySystemDataSets = res
      this.cdr.markForCheck()
    })

    this.keyingDataSetsService.keyingDataSets$
      .pipe(takeUntil(this.destroy$))
      .subscribe((keyingDataSets: KeyingDataSets) => {
        if (!keyingDataSets) {
          return
        }
        this.keyingDataSets = keyingDataSets
        this.keywayFamily = this.keyingDataSets.keywayFamilies.find((kwf) => kwf.id === this.structure?.keywayFamilyId)
        this.combinatingRule = this.keyingDataSets.combinatingRuleSets.find(
          (kwf) => kwf.id === this.structure?.combinatingRuleSetId
        )
        this.cdr.markForCheck()
      })
  }

  private onStructureLoad(structure: Structure) {
    this.keyingStructureService.selectStructure(structure)
    this.keyingSystemService.setKeySystems(structure.keySystems)
    this.keyingSystemService.setKeySystemById(this.keySystemId)

    this.keyingDataSetsService.loadStructureDataSets(structure)
    this.keyingDataSetsService.loadKeySystemDataSets(this.structureId, this.keySystemId)
    this.keyingDataSetsService.loadCombinatingRuleSets(structure.keywayFamilyId)
  }

  /* istanbul ignore next */
  ngOnDestroy() {
    this.keyingDataSetsService.setKeySystemDataSets(null)
    this.destroy$.next(true)
    this.destroy$.complete()
  }

  /* istanbul ignore next */
  profileAction(action) {
    switch (action.id) {
      case 0:
        this.keyingAuthService.logout()
        break
      default:
        break
    }
  }

  /* istanbul ignore next */
  queueForValidation(): void {
    this.loadingService.set({ isLoading: true, message: 'Queuing for MECS' })
    this.keyingSystemService.queueForValidation(this.keySystemId, this.structureId).subscribe(
      () => {
        this.loadingService.set({ isLoading: false, message: '' })
        this.router.navigate([AppRoutingConfig.structuresUrl])
      },
      (error) => {
        this.loadingService.set({ isLoading: false, message: '' })
        this.errorService.throw(error)
      }
    )
  }

  /* istanbul ignore next */
  helpAction(action) {
    switch (action.id) {
      case 1:
        this.openLicenceFile()
        break
      default:
        break
    }
  }

  /* istanbul ignore next */
  openLicenceFile() {
    this.dialog.open(ThirdPartyNoticeComponent)
  }

  /* istanbul ignore next */
  enableDisableMECS(): void {
    this.getStructureDetails()
    this.keyingDataSetsService.structureDetails$.pipe(takeUntil(this.destroy$)).subscribe((structureDetails) => {
      if (structureDetails) {
        this.enableDisableMECSButton(structureDetails)
        this.cdr.markForCheck()
      }
    })
  }

  /* istanbul ignore next */
  getStructureDetails() {
    this.keyingDataSetsService.loadStructureDetails(this.structure.id)
  }

  enableDisableMECSButton(structureDetails: StructureDetails) {
    if (!this.user || this.user._id !== this.keySystem.lockedById) {
      this.disableMecsForUnassignedUser()
      return
    }
    if (structureDetails.mecsInProgress) {
      this.disableMecsBtnIfMecsInProgress()
      return
    }
    if (structureDetails.publishingInProgress) {
      this.disableMecsBtnIfPublishingInProgress()
      return
    }
    if (structureDetails.pinningGenerationInprogress) {
      this.disableMecsBtnIfPinningGenerationInprogress(structureDetails.pinningGenerationPercentageOfCompletion)
      return
    }
    if (
      structureDetails.hasUnassignedKeys ||
      structureDetails.hasCylindersAwaitingUserAttention ||
      structureDetails.hasCylindersWithNoPinning
    ) {
      this.disableMecsBtnForAnyOtherReason()
      return
    }
    this.disableMecsBtn = false
    this.mecsToolTipMessage = ''
  }

  disableMecsForUnassignedUser() {
    this.disableMecsBtn = true
    this.mecsToolTipMessage = 'User is not assigned'
  }

  disableMecsBtnIfMecsInProgress() {
    this.disableMecsBtn = true
    this.mecsToolTipMessage = 'MECS in progress'
  }

  disableMecsBtnIfPublishingInProgress() {
    this.disableMecsBtn = true
    this.mecsToolTipMessage = 'Publishing in progress'
  }

  disableMecsBtnIfPinningGenerationInprogress(percentageOfCompletion: number) {
    this.disableMecsBtn = true
    this.mecsToolTipMessage = `Pinning generation in progress (${percentageOfCompletion}% complete)`
  }

  disableMecsBtnForAnyOtherReason() {
    this.disableMecsBtn = true
    this.mecsToolTipMessage =
      'Queue for MECS will be disabled until all keys are assigned and all cylinders are verified'
  }
}
